import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import { prepareCodesForSubmit, CodeRangeError } from "helpers/codeRanges";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCreateUnitCapMutation } from "store/queries/unitCap";

import {
  IUnitCapFormData,
  INITIAL_UNIT_CAP_FORM_DATA,
  UnitCapForm,
} from "./components/UnitCapForm";
import {
  isScopeComplete,
  scopeRequestFields,
} from "./helpers/ruleApplicability";

export function CreateUnitCapPage() {
  useDocumentTitle("Create Unit Cap Rule");
  const navigate = useNavigate();
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const [createMutation, { isLoading }] = useCreateUnitCapMutation();
  const [formData, setFormData] = useState<IUnitCapFormData>(
    INITIAL_UNIT_CAP_FORM_DATA
  );

  const handleSubmit = async () => {
    if (!isScopeComplete(formData)) return;
    let cptCodes: string[];
    try {
      cptCodes = prepareCodesForSubmit(formData.cptCodes);
    } catch (err) {
      toastElement?.show({
        severity: "error",
        summary: "Invalid code range",
        detail:
          err instanceof CodeRangeError
            ? err.message
            : "Failed to expand code ranges",
      });
      return;
    }
    setIsLoading(true);
    try {
      await createMutation({
        maxUnitsPerCode: formData.maxUnitsPerCode,
        cptCodes,
        groupName: formData.groupName.trim() || null,
        contractLanguage: formData.contractLanguage.trim() || null,
        // CLE-2524: applicability wins over the legacy isDefault flag.
        applicability: formData.applicability,
        ...scopeRequestFields(formData),
      }).unwrap();
      toastElement?.show({
        severity: "success",
        summary: "Created!",
        detail: "Unit Cap rule created successfully.",
      });
      navigate("/payer-rules");
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: error?.data?.message || "Failed to create unit cap rule",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <UnitCapForm
      title="Create Unit Cap Rule"
      submitLabel="Create Rule"
      formData={formData}
      onChange={setFormData}
      onSubmit={handleSubmit}
      isSubmitting={isLoading}
    />
  );
}
